import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useRouter } from '@tanstack/react-router';
import { Layers } from 'lucide-react';
import { z } from 'zod';
import { buttonVariants } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_MKT_COMMENT_MAX, PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { useStagePcmpMisBatch } from '../../hooks/useStagePcmpMisBatch';
import type { PcmpMisStageResult, PcmpMisStageSelection } from '../../types/pcmpMis.types';
import type { PcmpChannel } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';

const commentFormSchema = z.object({
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type CommentFormValues = z.infer<typeof commentFormSchema>;

export interface PcmpMisStageTarget {
  channel: PcmpChannel;
  count: number;
  amount: number;
  selection: PcmpMisStageSelection;
}

interface PcmpMisStageDialogProps {
  target: PcmpMisStageTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once the batch exists, so the caller can clear its selection. */
  onStaged: () => void;
  onClose: () => void;
}

/** Legacy MisRouteBulk: put the selection into a new routing batch for one payer. */
export function PcmpMisStageDialog({
  target,
  refNoOf,
  onStaged,
  onClose,
}: PcmpMisStageDialogProps) {
  const router = useRouter();
  const [outcome, setOutcome] = useState<PcmpMisStageResult | null>(null);
  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { comment: '' },
  });

  const openBatch = (batchNo: number) =>
    void router.navigate({ to: '/pcmp/mis/batches/$batchNo', params: { batchNo } });

  const { mutate, isPending, error } = useStagePcmpMisBatch((result) => {
    onStaged();
    if (result.skipped.length > 0) setOutcome(result);
    else openBatch(result.batchNo);
  });

  const onSubmit = form.handleSubmit((values) =>
    mutate({ channel: target.channel, comment: values.comment, ...target.selection }),
  );

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title={`Batch #${outcome.batchNo} staged`}
        summary={`${formatCount(outcome.staged.length, 'proposal')} staged for ${
          PCMP_CHANNEL_LABELS[outcome.channel]
        }. The ones below changed since the list was loaded and were left out.`}
        skipped={outcome.skipped}
        refNoOf={refNoOf}
        actions={
          <Link
            to="/pcmp/mis/batches/$batchNo"
            params={{ batchNo: outcome.batchNo }}
            className={buttonVariants()}
          >
            Open batch #{outcome.batchNo}
          </Link>
        }
        onClose={onClose}
      />
    );
  }

  const payer = PCMP_CHANNEL_LABELS[target.channel];

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Stage for ${payer}`}
        description="New routing batch"
        icon={Layers}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Stage batch"
        pendingLabel="Staging..."
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">{payer} pays</span>
          <span className="font-medium">
            {formatCount(target.count, 'proposal')} · {formatTaka(target.amount)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          The batch stays staged until it is published. Proposals that changed meanwhile (held, no
          longer verified, already batched) are left out and listed.
        </p>
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Why these proposals go together"
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
